import { useMemo } from 'react';

import { Apollo } from 'src/gql/generated';
import { useMyOnboardingInfoQuery } from 'src/hooks';

export enum QuickStartTitle {
  AIPlaybooks = 'AI Playbooks',
  AlineWorkflows = 'Aline Workflows',
  GetToKnowAline = 'Get to know Aline',
  SetUpAIRepository = 'Setup AI Repository',
}

interface OnboardingTaskWithTime extends Apollo.OnboardingTask {
  numMins: number;
}

export interface OnboardingStep {
  tasks: OnboardingTaskWithTime[];
  title: QuickStartTitle;
}

export interface OnboardingSections {
  quickStart: OnboardingStep[];
}

export function useOnboarding() {
  const { loading, onboardingTasks } = useMyOnboardingInfoQuery();
  const sections = useMemo<OnboardingSections>(() => {
    /**
     * ToDo: Add back in QuickStartTitle.AlineWorkflows when the tour is fixed.
     */
    const quickStart = [
      QuickStartTitle.GetToKnowAline,
      QuickStartTitle.SetUpAIRepository,
      QuickStartTitle.AIPlaybooks,
    ].map(title => {
      return {
        tasks: getRelevantOnboardingTasks({ stepTitle: title, tasks: onboardingTasks }),
        title,
      };
    });

    return {
      quickStart,
    };
  }, [onboardingTasks]);

  return { loading, sections };
}

function getRelevantOnboardingTasks({
  stepTitle,
  tasks,
}: {
  stepTitle: QuickStartTitle;
  tasks: Apollo.OnboardingTask[];
}): OnboardingTaskWithTime[] {
  switch (stepTitle) {
    case QuickStartTitle.GetToKnowAline: {
      return [Apollo.OnboardingTaskAction.AlineIntroVideo].flatMap(action => {
        const relevantTask = tasks.find(task => task.action === action);
        return relevantTask
          ? [{ ...relevantTask, numMins: getOnboardingActionTime(relevantTask) }]
          : [];
      });
    }

    case QuickStartTitle.AIPlaybooks: {
      return [
        Apollo.OnboardingTaskAction.LaunchExampleNdaPlaybook,
        Apollo.OnboardingTaskAction.GenerateAiPlaybook,
      ].flatMap(action => {
        const relevantTask = tasks.find(task => task.action === action);
        return relevantTask
          ? [{ ...relevantTask, numMins: getOnboardingActionTime(relevantTask) }]
          : [];
      });
    }

    case QuickStartTitle.AlineWorkflows: {
      return [Apollo.OnboardingTaskAction.SignPdf].flatMap(action => {
        const relevantTask = tasks.find(task => task.action === action);
        return relevantTask
          ? [{ ...relevantTask, numMins: getOnboardingActionTime(relevantTask) }]
          : [];
      });
    }

    case QuickStartTitle.SetUpAIRepository: {
      return [
        Apollo.OnboardingTaskAction.ImportLegacyIntoRepo,
        Apollo.OnboardingTaskAction.AlineAiExampleReport,
      ].flatMap(action => {
        const relevantTask = tasks.find(task => task.action === action);
        return relevantTask
          ? [{ ...relevantTask, numMins: getOnboardingActionTime(relevantTask) }]
          : [];
      });
    }
  }
}

function getOnboardingActionTime(task: Apollo.OnboardingTask): number {
  switch (task.action) {
    case Apollo.OnboardingTaskAction.GenerateAiPlaybook:
    case Apollo.OnboardingTaskAction.ImportLegacyIntoRepo:
    case Apollo.OnboardingTaskAction.AlineAiExampleReport:
    case Apollo.OnboardingTaskAction.LaunchExampleNdaPlaybook:
      return 2;
    case Apollo.OnboardingTaskAction.SignPdf:
    case Apollo.OnboardingTaskAction.AlineIntroVideo:
      return 5;
  }
}
